/**
 * React Query layer for user language preference.
 * Provides query and mutation hooks with optimistic updates.
 *
 * Service layer handles mock/API switching
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { SupportedLanguage } from "@/constants/languages";
import { LoginRoutes } from "@/routes/config";
import { userPreferenceService } from "@/infrastructure/services";
import { isAuth } from "@/utils/api";
import {
  UserLanguagePreference,
  UserStudySettings,
  UpdateStudySettingsPayload,
} from "@/infrastructure/adapters/user-preference/user-preference.types";

/** Check if current route requires auth AND user has valid token */
const isAuthenticatedRoute = () => !LoginRoutes.some((route) => window.location.pathname.startsWith(route)) && isAuth();

/**
 * Query: Fetch user language preference
 * Uses service layer - automatically uses mock or API based on service registry
 */
export const useLanguageQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_PREFERENCE.LANGUAGE],
    queryFn: () => userPreferenceService.getLanguage(),
    enabled: isAuthenticatedRoute(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * Mutation: Update user language preference
 * Implements optimistic update with rollback on error
 */
export const useLanguageMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (language: SupportedLanguage) => userPreferenceService.setLanguage(language),

    onMutate: async (newLanguage) => {
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.USER_PREFERENCE.LANGUAGE],
      });

      const previousData = queryClient.getQueryData<UserLanguagePreference>([QUERY_KEYS.USER_PREFERENCE.LANGUAGE]);

      queryClient.setQueryData<UserLanguagePreference>([QUERY_KEYS.USER_PREFERENCE.LANGUAGE], (old) =>
        old ? { ...old, language: newLanguage, updatedAt: new Date() } : { language: newLanguage, updatedAt: new Date() },
      );

      return { previousData };
    },

    onError: (_error, _newLanguage, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([QUERY_KEYS.USER_PREFERENCE.LANGUAGE], context.previousData);
      }
    },

    // No refetch — optimistic update is sufficient, PATCH syncs to BE
  });
};

/**
 * Query: Fetch user study settings
 */
export const useStudySettingsQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.USER_PREFERENCE.STUDY_SETTINGS],
    queryFn: () => userPreferenceService.getStudySettings(),
    enabled: isAuthenticatedRoute(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * Mutation: Update user study settings
 */
export const useStudySettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: UpdateStudySettingsPayload) => userPreferenceService.updateStudySettings(settings),

    onMutate: async (newSettings) => {
      // Skip optimistic update when weeklyStudyTimeTarget changes — the dashboard
      // transition would fire the weekly API before the BE has the new target.
      if (Object.prototype.hasOwnProperty.call(newSettings, "weeklyStudyTimeTarget")) {
        return {};
      }

      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.USER_PREFERENCE.STUDY_SETTINGS],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<UserStudySettings>([QUERY_KEYS.USER_PREFERENCE.STUDY_SETTINGS]);

      // Optimistically update to the new value
      queryClient.setQueryData<UserStudySettings>([QUERY_KEYS.USER_PREFERENCE.STUDY_SETTINGS], (old) => {
        if (!old) return old;
        return { ...old, ...newSettings };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },

    onSuccess: (_data, newSettings) => {
      // For weeklyStudyTimeTarget changes, update cache only after API confirms
      if (Object.prototype.hasOwnProperty.call(newSettings, "weeklyStudyTimeTarget")) {
        queryClient.setQueryData<UserStudySettings>([QUERY_KEYS.USER_PREFERENCE.STUDY_SETTINGS], (old) => {
          if (!old) return old;
          return { ...old, ...newSettings };
        });
      }
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_error, _newSettings, context) => {
      if (context?.previousData) {
        queryClient.setQueryData([QUERY_KEYS.USER_PREFERENCE.STUDY_SETTINGS], context.previousData);
      }
    },

    // No refetch — optimistic update is sufficient, PATCH syncs to BE
  });
};
